/**
 * Sentry observability layer — BizCore Superadmin.
 *
 * Env-gated Sentry SDK wrapper for error monitoring, performance tracing, and session replay.
 * Mirrors the BizCore Tenant's sentry.ts API surface adapted to JSX conventions.
 *
 * Design rules (same posture as the tenant clarity/sentry layers):
 *  - NEVER throws or blocks UI. Every public fn is a no-op when disabled.
 *  - Disabled by default outside production unless VITE_ENABLE_SENTRY=true.
 *  - Never sends PII: error messages are truncated, endpoint paths normalized.
 */

// ── Environment ──────────────────────────────────────────────────────────────
const ENV = {
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enableFlag: import.meta.env.VITE_ENABLE_SENTRY,
  environment: import.meta.env.VITE_APP_ENV || import.meta.env.MODE || 'development',
  appVersion: import.meta.env.VITE_APP_VERSION || '0.0.0',
  releaseSha: import.meta.env.VITE_RELEASE_SHA || 'dev',
  mode: import.meta.env.MODE,
  tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || 0.2),
  replaysSessionSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || 0.1),
  replaysOnErrorSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || 1.0),
};

const TEST_FLAG = '__bizcore_sentry_force__';
let cachedEnabled;

function isSentryEnabled() {
  if (cachedEnabled !== undefined) return cachedEnabled;
  cachedEnabled = computeEnabled();
  return cachedEnabled;
}

function computeEnabled() {
  if (typeof window === 'undefined') return false;
  if (!ENV.dsn) return false;
  if (ENV.enableFlag === 'false') return false;
  if (ENV.mode === 'production') return true;
  if (ENV.enableFlag === 'true') return true;
  // Runtime override for E2E tests
  try { return window.localStorage.getItem(TEST_FLAG) === '1'; } catch { return false; }
}

/** Test-only: reset enabled cache */
export function __resetSentryEnabled() { cachedEnabled = undefined; }

// ── Helpers ──────────────────────────────────────────────────────────────────
/** Normalise a URL path: strip query and replace id-like segments with `:id`. */
function normalizeEndpoint(rawPath) {
  if (!rawPath) return '/';
  const path = rawPath.split('?')[0].split('#')[0];
  const segments = path.split('/').map(seg =>
    /^\d+$/.test(seg) || /^[0-9a-f]{8}-[0-9a-f-]+$/i.test(seg) || seg.length > 24 ? ':id' : seg
  );
  return segments.join('/') || '/';
}

/** Bucket a millisecond duration into a coarse label. */
function durationBucket(ms) {
  if (ms < 1000) return 'lt_1s';
  if (ms < 3000) return '1_3s';
  if (ms < 10000) return '3_10s';
  if (ms < 30000) return '10_30s';
  if (ms < 60000) return '30_60s';
  return 'gt_60s';
}

/** Sanitize a tag value: truncate to avoid cardinality/PII issues. */
function sanitize(val) {
  if (val === null || val === undefined) return undefined;
  const s = String(val).trim();
  if (!s) return undefined;
  return s.length > 100 ? s.slice(0, 100) : s;
}

// ── Init ─────────────────────────────────────────────────────────────────────
let initialized = false;

/**
 * Call ONCE at app entry, before React renders. Imports @sentry/react dynamically
 * so the SDK bundle is loaded only when Sentry is actually enabled.
 */
export async function initSentry() {
  if (initialized || !isSentryEnabled()) return;
  initialized = true;

  try {
    const Sentry = await import('@sentry/react');

    Sentry.init({
      dsn: ENV.dsn,
      environment: ENV.environment,
      release: ENV.appVersion,

      tracesSampleRate: ENV.tracesSampleRate,
      tracePropagationTargets: ['localhost', /^https:\/\/[^/]+\.ingest\.us\.sentry\.io/],

      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
      ],
      replaysSessionSampleRate: ENV.replaysSessionSampleRate,
      replaysOnErrorSampleRate: ENV.replaysOnErrorSampleRate,
      enableLogs: true,

      dataCollection: {
        userInfo: false,
        httpBodies: [],
        queryParams: false,
      },
    });

    Sentry.setTag('app_name', 'bizcore-superadmin');
    Sentry.setTag('app_env', ENV.environment);
    Sentry.setTag('release_sha', ENV.releaseSha);

    installGlobalListeners(Sentry);
  } catch (e) {
    console.warn('[Sentry] init failed (non-fatal):', e);
  }
}

function installGlobalListeners(Sentry) {
  if (typeof window === 'undefined') return;
  window.addEventListener('unhandledrejection', (e) => {
    try {
      if (e.reason instanceof Error) Sentry.captureException(e.reason, { tags: { source: 'unhandledrejection' } });
      else Sentry.captureMessage(`Unhandled rejection: ${String(e.reason)}`, { level: 'error', tags: { source: 'unhandledrejection' } });
    } catch { /* noop */ }
  });
}

// ── User identity ────────────────────────────────────────────────────────────
export function identifySentryUser(ctx) {
  if (!isSentryEnabled()) return;
  try {
    import('@sentry/react').then(Sentry => {
      if (ctx) Sentry.setUser({ id: sanitize(ctx.id), role: sanitize(ctx.role) });
      else Sentry.setUser(null);
    });
  } catch { /* noop */ }
}

// ── Tags ─────────────────────────────────────────────────────────────────────
export function setSentryTags(tags) {
  if (!isSentryEnabled() || !tags) return;
  try {
    import('@sentry/react').then(Sentry => {
      for (const [k, v] of Object.entries(tags)) {
        const safe = sanitize(v);
        if (safe !== undefined) Sentry.setTag(k, safe);
      }
    });
  } catch { /* noop */ }
}

// ── Route context ────────────────────────────────────────────────────────────
export function setSentryRoute(pathname, authState) {
  if (!isSentryEnabled()) return;
  try {
    import('@sentry/react').then(Sentry => {
      const norm = normalizeEndpoint(pathname);
      Sentry.setTag('route_id', norm);
      Sentry.setTag('route_group', norm.split('/')[1] || '/');
      if (authState) Sentry.setTag('auth_state', authState);
    });
  } catch { /* noop */ }
}

// ── Error capture ────────────────────────────────────────────────────────────
export function captureException(error, context) {
  if (!isSentryEnabled()) return;
  try {
    import('@sentry/react').then(Sentry => {
      Sentry.withScope(scope => {
        if (context?.area) scope.setTag('error_area', context.area);
        if (context?.route) scope.setTag('route_id', context.route);
        if (context?.level) scope.setLevel(context.level);
        if (context?.tags) Object.entries(context.tags).forEach(([k, v]) => scope.setTag(k, v));
        if (context?.extra) scope.setContext('extra', context.extra);

        if (typeof error === 'string') Sentry.captureMessage(error, context?.level || 'error');
        else Sentry.captureException(error);
      });
    });
  } catch { /* noop */ }
}

// ── API failure ──────────────────────────────────────────────────────────────
export function captureApiFailure(ctx) {
  if (!isSentryEnabled()) return;
  try {
    import('@sentry/react').then(Sentry => {
      const endpoint = `${ctx.method?.toUpperCase() || 'GET'} ${normalizeEndpoint(ctx.path)}`;
      const statusClass = ctx.isTimeout ? 'timeout' : !ctx.status ? 'network' : `${Math.floor(ctx.status / 100)}xx`;
      const correlationId = sanitize(ctx.correlationId);

      Sentry.addBreadcrumb({
        category: 'api',
        message: `${ctx.method || 'GET'} ${normalizeEndpoint(ctx.path)}`,
        level: ctx.status >= 500 ? 'error' : 'warning',
        data: { method: ctx.method, endpoint: normalizeEndpoint(ctx.path), status: ctx.status, statusClass, correlationId },
      });

      if (ctx.status >= 500) {
        const err = new Error(ctx.message || `API ${ctx.status} on ${endpoint}`);
        err.name = 'ApiError';
        const tags = { http_status: String(ctx.status), endpoint };
        if (correlationId) tags.correlation_id = correlationId;
        captureException(err, { area: 'api', level: 'error', tags });
      }
    });
  } catch { /* noop */ }
}

// ── Performance ──────────────────────────────────────────────────────────────
export function capturePerformance(ctx) {
  if (!isSentryEnabled()) return;
  try {
    import('@sentry/react').then(Sentry => {
      const bucket = durationBucket(ctx.durationMs);
      if (bucket === 'lt_1s' || bucket === '1_3s') return;
      Sentry.addBreadcrumb({
        category: 'performance',
        message: `${ctx.kind || 'interaction'} ${bucket}`,
        level: 'warning',
        data: { kind: ctx.kind, durationMs: ctx.durationMs, bucket, label: ctx.label },
      });
    });
  } catch { /* noop */ }
}

// ── Breadcrumb ───────────────────────────────────────────────────────────────
export function addBreadcrumb(category, message, data) {
  if (!isSentryEnabled()) return;
  try {
    import('@sentry/react').then(Sentry => Sentry.addBreadcrumb({ category, message, level: 'info', data }));
  } catch { /* noop */ }
}

// ── React integration helpers ────────────────────────────────────────────────

/**
 * Create the Sentry React error handler for use in createRoot (React 19 pattern).
 * Returns a no-op when Sentry is disabled.
 */
export async function createSentryErrorHandler() {
  if (!isSentryEnabled()) {
    return () => {};
  }
  try {
    const Sentry = await import('@sentry/react');
    return Sentry.reactErrorHandler();
  } catch {
    return () => {};
  }
}


